// src/admin/pages/purchase-orders/rawMaterialService.ts
//
// Raw materials & other supplies inbound through purchase orders.
//
// Unlike catalogue products (which live in `products` + `warehouse_inventory`),
// raw materials are tracked in a dedicated `rawMaterials` collection. Each doc
// keeps a running stock quantity, a weighted-average unit cost, and a per-
// warehouse stock breakdown. Receipts are logged to a `receipts` subcollection
// for traceability.
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  query,
  where,
  orderBy,
  runTransaction,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';

const RAW_MATERIALS_COLLECTION = 'rawMaterials';

export interface RawMaterialWarehouseStock {
  warehouseId: string;
  warehouseName: string;
  quantity: number;
}

export interface RawMaterial {
  id?: string;
  name: string;
  sku: string;
  unit: string;
  description?: string;
  isActive: boolean;
  stockQuantity: number;         // total across warehouses
  avgCost: number;               // weighted-average unit cost
  warehouseStock?: RawMaterialWarehouseStock[];
  createdAt?: Date;
  updatedAt?: Date;
}

const normalizeKey = (sku: string, name: string): string =>
  (sku && sku.trim() ? sku : name).trim().toLowerCase();

export class RawMaterialService {
  static async getAllRawMaterials(activeOnly = false): Promise<RawMaterial[]> {
    try {
      const ref = collection(db, RAW_MATERIALS_COLLECTION);
      const q = activeOnly
        ? query(ref, where('isActive', '==', true), orderBy('name', 'asc'))
        : query(ref, orderBy('name', 'asc'));
      const snap = await getDocs(q);
      return snap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          name: data.name || '',
          sku: data.sku || '',
          unit: data.unit || '',
          description: data.description || '',
          isActive: data.isActive !== false,
          stockQuantity: data.stockQuantity || 0,
          avgCost: data.avgCost || 0,
          warehouseStock: data.warehouseStock || [],
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as RawMaterial;
      });
    } catch (error) {
      console.error('Error fetching raw materials:', error);
      throw error;
    }
  }

  private static async findByKey(sku: string, name: string): Promise<{ id: string } | null> {
    const ref = collection(db, RAW_MATERIALS_COLLECTION);
    const key = normalizeKey(sku, name);
    // Prefer an SKU match; fall back to name match.
    if (sku && sku.trim()) {
      const snap = await getDocs(query(ref, where('sku', '==', sku.trim())));
      if (!snap.empty) return { id: snap.docs[0].id };
    }
    const nameSnap = await getDocs(query(ref, where('name', '==', name.trim())));
    if (!nameSnap.empty) return { id: nameSnap.docs[0].id };
    // last resort: scan for normalized key (handles casing differences)
    const all = await getDocs(ref);
    const match = all.docs.find(d => normalizeKey(d.data().sku || '', d.data().name || '') === key);
    return match ? { id: match.id } : null;
  }

  /**
   * Receive a raw material into inventory. Creates the master record on first
   * receipt, otherwise increments stock and recomputes the weighted-average cost.
   * A receipt log entry is appended for traceability.
   */
  static async receiveRawMaterial(params: {
    name: string;
    sku: string;
    unit: string;
    quantity: number;
    unitCost: number;
    warehouseId: string;
    warehouseName: string;
    purchaseOrderId?: string;
    purchaseOrderNumber?: string;
  }): Promise<string> {
    const { name, sku, unit, quantity, unitCost, warehouseId, warehouseName } = params;
    if (quantity <= 0) throw new Error('Raw material quantity must be greater than 0');

    const existing = await this.findByKey(sku, name);
    const materialRef = existing
      ? doc(db, RAW_MATERIALS_COLLECTION, existing.id)
      : doc(collection(db, RAW_MATERIALS_COLLECTION));

    await runTransaction(db, async (tx) => {
      const snap = existing ? await tx.get(materialRef) : null;

      if (snap && snap.exists()) {
        const data = snap.data();
        const prevQty = data.stockQuantity || 0;
        const prevAvg = data.avgCost || 0;
        const newQty = prevQty + quantity;
        // Weighted-average cost across old stock + this receipt.
        const newAvg = newQty > 0 ? ((prevQty * prevAvg) + (quantity * unitCost)) / newQty : unitCost;

        const whStock: RawMaterialWarehouseStock[] = data.warehouseStock || [];
        const idx = whStock.findIndex(w => w.warehouseId === warehouseId);
        if (idx >= 0) whStock[idx].quantity += quantity;
        else whStock.push({ warehouseId, warehouseName, quantity });

        tx.update(materialRef, {
          stockQuantity: newQty,
          avgCost: newAvg,
          warehouseStock: whStock,
          unit: unit || data.unit || '',
          updatedAt: serverTimestamp(),
        });
      } else {
        tx.set(materialRef, {
          name: name.trim(),
          sku: (sku || '').trim(),
          unit: unit || '',
          description: '',
          isActive: true,
          stockQuantity: quantity,
          avgCost: unitCost,
          warehouseStock: [{ warehouseId, warehouseName, quantity }],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    });

    // Receipt log (outside the transaction — best-effort audit trail).
    try {
      await addDoc(collection(materialRef, 'receipts'), {
        quantity,
        unitCost,
        warehouseId,
        warehouseName,
        purchaseOrderId: params.purchaseOrderId || null,
        purchaseOrderNumber: params.purchaseOrderNumber || null,
        receivedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Raw material receipt log failed (non-fatal):', err);
    }

    return materialRef.id;
  }
}
